import React, { memo } from 'react';
import { CheckCircle2, Heart, Shield, BookOpen, MonitorSmartphone, Smartphone, Tablet, Monitor, Gift, Clock, Lock, Download, ChevronDown, Star, Activity, Users, Brain, AlertCircle, Coffee, Check } from 'lucide-react';
import { motion } from 'framer-motion';
import { PurchaseNotification } from '../components/PurchaseNotification';
const FadeIn = ({
  children,
  delay = 0
}: {
  children: React.ReactNode;
  delay?: number;
}) => <motion.div initial={{
  opacity: 0,
  y: 20
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-50px'
}} transition={{
  duration: 0.6,
  delay
}} data-id="element-19">
    {children}
  </motion.div>;
const CHECKOUT_URL = 'https://checkout.complementodigital.shop/cart/40809362620545:1';
export function LandingPage() {
  const scrollToPricing = () => {
    document.getElementById('oferta')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const goToCheckout = () => {
    window.location.href = CHECKOUT_URL;
  };
  return <div className="w-full bg-pagebg overflow-hidden" data-id="element-20">
      {/* HERO SECTION */}
      <section className="relative pt-16 pb-24 lg:pt-24 lg:pb-32 px-6" data-id="element-21">
        <div className="max-w-3xl mx-auto text-center flex flex-col items-center" data-id="element-22">
          <FadeIn data-id="element-23">
            <div className="inline-block bg-secondary/10 text-secondary font-semibold px-4 py-2 rounded-full text-sm mb-6" data-id="element-24">
              Programa Digital Completo
            </div>
            <h1 className="text-4xl lg:text-5xl font-heading font-bold leading-tight text-title" data-id="element-25">
              Mega Pack del{' '}
              <span className="text-primary" data-id="element-26">Cuidador Profesional</span>
            </h1>
          </FadeIn>

          {/* Product mockup — displayed at 70% of its size, below the title */}
          <FadeIn delay={0.15} data-id="element-27">
            <img src="https://cdn.magicpatterns.com/uploads/e1534Tzu2VxUnEDsa92bzf/mockup_cuidador-Photoroom.png" alt="Mega Pack del Cuidador Profesional — módulos, bonos y recursos digitales" className="w-[70%] max-w-md mx-auto my-8 drop-shadow-2xl" data-id="element-28" />
          </FadeIn>

          <FadeIn delay={0.25} data-id="element-29">
            <div className="space-y-6 flex flex-col items-center" data-id="element-30">
              <p className="text-lg text-body leading-relaxed" data-id="element-31">
                Todo lo que necesitás para aprender a cuidar adultos mayores con
                seguridad, confianza y profesionalismo, desde la comodidad de tu
                hogar.
              </p>
              <p className="text-base text-body leading-relaxed" data-id="element-32">
                Aprendé paso a paso con un programa digital completo que reúne
                los conocimientos esenciales para brindar un cuidado
                responsable, humano y organizado, incluso si estás comenzando
                desde cero.
              </p>

              <ul className="space-y-3 text-left inline-flex flex-col" data-id="element-33">
                {['Acceso inmediato', 'Estudiá a tu ritmo', 'Material descargable', 'Más de 12 recursos exclusivos'].map((item, i) => <li key={i} className="flex items-center gap-3 text-title font-medium" data-id="element-34">
                    <CheckCircle2 className="text-secondary w-6 h-6 flex-shrink-0" data-id="element-35" />
                    {item}
                  </li>)}
              </ul>

              <button onClick={scrollToPricing} className="w-full sm:w-auto bg-accent hover:bg-accent-hover text-white font-heading font-bold text-lg px-8 py-4 rounded-2xl shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1 flex items-center justify-center gap-2" data-id="element-36">
                👉 QUIERO ACCEDER AHORA
              </button>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* SECTION 2: ¿Este Mega Pack es para vos? */}
      <section className="py-20 bg-white px-6" data-id="element-37">
        <div className="max-w-4xl mx-auto" data-id="element-38">
          <FadeIn data-id="element-39">
            <div className="text-center mb-12" data-id="element-40">
              <h2 className="text-3xl lg:text-4xl font-heading font-bold text-title mb-4" data-id="element-41">
                ¿Este Mega Pack es para vos?
              </h2>
              <p className="text-lg text-body" data-id="element-42">
                Este programa fue creado para personas que:
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-6" data-id="element-43">
              {['Quieren aprender a cuidar adultos mayores.', 'Están comenzando como cuidadores.', 'Ya trabajan como cuidadores.', 'Quieren mejorar sus conocimientos.', 'Buscan brindar un cuidado más seguro.', 'Desean conseguir más oportunidades laborales.'].map((text, i) => <div key={i} className="flex items-start gap-4 p-4 rounded-2xl bg-pagebg border border-gray-100 hover:border-primary/20 transition-colors" data-id="element-44">
                  <div className="bg-primary/10 p-2 rounded-full mt-1" data-id="element-45">
                    <Check className="w-5 h-5 text-primary" data-id="element-46" />
                  </div>
                  <p className="text-title font-medium leading-relaxed" data-id="element-47">
                    {text}
                  </p>
                </div>)}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* SECTION 3: ¿Qué vas a lograr? */}
      <section className="py-20 px-6" data-id="element-48">
        <div className="max-w-6xl mx-auto" data-id="element-49">
          <FadeIn data-id="element-50">
            <div className="text-center mb-16" data-id="element-51">
              <h2 className="text-3xl lg:text-4xl font-heading font-bold text-title mb-4" data-id="element-52">
                ¿Qué vas a lograr?
              </h2>
              <p className="text-lg text-body" data-id="element-53">
                Al finalizar este programa podrás:
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-54">
              {[{
              icon: Brain,
              text: 'Comprender mejor las necesidades del adulto mayor.'
            }, {
              icon: Heart,
              text: 'Brindar cuidados diarios correctamente.'
            }, {
              icon: Activity,
              text: 'Movilizar personas de forma segura.'
            }, {
              icon: Shield,
              text: 'Actuar con mayor confianza.'
            }, {
              icon: Users,
              text: 'Comunicarte mejor con personas con demencia.'
            }, {
              icon: Clock,
              text: 'Organizar tu trabajo profesionalmente.'
            }, {
              icon: Star,
              text: 'Sentirte preparado para cuidar.'
            }].map((item, i) => <div key={i} className="bg-card p-8 rounded-3xl shadow-card hover:shadow-soft transition-shadow border border-gray-50 flex flex-col items-center text-center gap-4" data-id="element-55">
                  <div className="bg-secondary/10 p-4 rounded-2xl" data-id="element-56">
                    <item.icon className="w-8 h-8 text-secondary" data-id="element-57" />
                  </div>
                  <p className="text-title font-medium text-lg" data-id="element-58">{item.text}</p>
                </div>)}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* SECTION 4: Todo lo que incluye */}
      <section className="py-20 bg-white px-6" data-id="element-59">
        <div className="max-w-5xl mx-auto" data-id="element-60">
          <FadeIn data-id="element-61">
            <div className="text-center mb-16" data-id="element-62">
              <h2 className="text-3xl lg:text-4xl font-heading font-bold text-title mb-4" data-id="element-63">
                Todo lo que incluye el Mega Pack
              </h2>
              <p className="text-lg text-body" data-id="element-64">
                Un recorrido completo por todas las áreas del cuidado
              </p>
            </div>

            <div className="space-y-6" data-id="element-65">
              {[{
              mod: 'Módulo 1',
              title: 'Fundamentos del cuidado del adulto mayor',
              desc: 'Aprendé las bases del cuidado profesional, el rol del cuidador, la ética, los cambios propios del envejecimiento y cómo generar confianza con la familia.',
              icon: BookOpen
            }, {
              mod: 'Módulo 2',
              title: 'Higiene, confort y cuidados diarios',
              desc: 'Conocé los cuidados esenciales para brindar higiene, bienestar y prevenir complicaciones.',
              icon: Heart
            }, {
              mod: 'Módulo 3',
              title: 'Movilización segura y prevención de caídas',
              desc: 'Aprendé técnicas seguras para movilizar personas evitando lesiones y caídas.',
              icon: Activity
            }, {
              mod: 'Módulo 4',
              title: 'Alimentación, hidratación y administración segura de medicamentos',
              desc: 'Descubrí cómo organizar una alimentación adecuada, favorecer la hidratación y colaborar con la administración de medicamentos según indicación profesional.',
              icon: Coffee
            }, {
              mod: 'Módulo 5',
              title: 'Demencias, Alzheimer y comunicación efectiva',
              desc: 'Comprendé mejor las demencias y aprendé estrategias para comunicarte con empatía y seguridad.',
              icon: Brain
            }, {
              mod: 'Módulo 6',
              title: 'Emergencias y situaciones difíciles',
              desc: 'Aprendé cómo actuar frente a las situaciones más frecuentes mientras llega la ayuda médica.',
              icon: AlertCircle
            }].map((mod, i) => <div key={i} className="flex flex-col md:flex-row gap-6 bg-pagebg p-8 rounded-3xl border border-gray-100 hover:border-primary/30 transition-colors" data-id="element-66">
                  <div className="flex-shrink-0" data-id="element-67">
                    <div className="bg-primary text-white w-16 h-16 rounded-2xl flex items-center justify-center shadow-lg" data-id="element-68">
                      <mod.icon className="w-8 h-8" data-id="element-69" />
                    </div>
                  </div>
                  <div data-id="element-70">
                    <div className="text-secondary font-bold text-sm uppercase tracking-wider mb-2" data-id="element-71">
                      {mod.mod}
                    </div>
                    <h3 className="text-xl font-heading font-bold text-title mb-3" data-id="element-72">
                      {mod.title}
                    </h3>
                    <p className="text-body leading-relaxed" data-id="element-73">{mod.desc}</p>
                  </div>
                </div>)}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* SECTION 5: Bonos */}
      <section className="py-20 px-6" data-id="element-74">
        <div className="max-w-6xl mx-auto" data-id="element-75">
          <FadeIn data-id="element-76">
            <div className="text-center mb-16" data-id="element-77">
              <div className="inline-flex items-center justify-center gap-2 bg-accent/10 text-accent font-bold px-6 py-2 rounded-full mb-6" data-id="element-78">
                <Gift className="w-5 h-5" data-id="element-79" />
                REGALOS INCLUIDOS
              </div>
              <h2 className="text-3xl lg:text-4xl font-heading font-bold text-title" data-id="element-80">
                Además recibís 6 Bonos Exclusivos
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-81">
              {[{
              title: 'Kit de Plantillas Imprimibles',
              desc: 'Plantillas listas para imprimir que te ayudarán a organizar el cuidado diario de forma práctica.'
            }, {
              title: 'Manual Ilustrado de Movilización Segura',
              desc: 'Aprendé técnicas mediante ilustraciones claras y explicaciones paso a paso.'
            }, {
              title: 'Guía de Actividades para Estimular la Memoria',
              desc: 'Una colección de actividades para favorecer la memoria, la atención y el bienestar cognitivo.'
            }, {
              title: 'Cómo Conseguir Trabajo como Cuidador',
              desc: 'Consejos para preparar un currículum profesional y destacarte en entrevistas laborales.'
            }, {
              title: 'Guía Práctica sobre Alzheimer',
              desc: 'Conocé sus etapas, comprendé los cambios y aprendé estrategias para brindar un mejor cuidado.'
            }, {
              title: 'Guía Práctica de Primeros Auxilios',
              desc: 'Aprendé cómo actuar correctamente frente a las emergencias más frecuentes.'
            }].map((bono, i) => <div key={i} className="bg-card p-8 rounded-3xl shadow-card border-t-4 border-accent relative overflow-hidden group" data-id="element-82">
                  <div className="absolute -right-4 -top-4 opacity-5 group-hover:opacity-10 transition-opacity" data-id="element-83">
                    <Gift className="w-32 h-32 text-accent" data-id="element-84" />
                  </div>
                  <div className="flex items-center gap-3 mb-4" data-id="element-85">
                    <div className="bg-accent/20 p-2 rounded-lg" data-id="element-86">
                      <Gift className="w-5 h-5 text-accent" data-id="element-87" />
                    </div>
                    <span className="font-heading font-bold text-title" data-id="element-88">
                      Bonus {i + 1}
                    </span>
                  </div>
                  <h3 className="text-lg font-bold text-title mb-3 leading-snug" data-id="element-89">
                    {bono.title}
                  </h3>
                  <p className="text-body text-sm leading-relaxed" data-id="element-90">
                    {bono.desc}
                  </p>
                </div>)}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* SECTION 6: ¿Por qué elegir? */}
      <section className="py-24 bg-primary px-6 relative overflow-hidden" data-id="element-91">
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-white via-transparent to-transparent" data-id="element-92"></div>
        <div className="max-w-4xl mx-auto text-center relative z-10" data-id="element-93">
          <FadeIn data-id="element-94">
            <Shield className="w-16 h-16 text-secondary mx-auto mb-8" data-id="element-95" />
            <h2 className="text-3xl lg:text-4xl font-heading font-bold text-white mb-8" data-id="element-96">
              ¿Por qué elegir este Mega Pack?
            </h2>
            <div className="space-y-6 text-white/90 text-lg md:text-xl leading-relaxed font-medium" data-id="element-97">
              <p data-id="element-98">Porque no estás comprando un simple ebook.</p>
              <p data-id="element-99">
                Estás accediendo a un programa completo que reúne los
                conocimientos fundamentales para brindar un cuidado seguro,
                organizado y profesional.
              </p>
              <p data-id="element-100">
                Todo el contenido fue diseñado para ayudarte a adquirir
                confianza y mejorar tus habilidades como cuidador.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* SECTION 7: Dispositivos */}
      <section className="py-20 bg-white px-6" data-id="element-101">
        <div className="max-w-4xl mx-auto text-center" data-id="element-102">
          <FadeIn data-id="element-103">
            <h2 className="text-3xl font-heading font-bold text-title mb-12" data-id="element-104">
              Estudiá donde quieras
            </h2>
            <div className="flex flex-wrap justify-center gap-12 mb-12" data-id="element-105">
              <div className="flex flex-col items-center gap-4" data-id="element-106">
                <div className="bg-pagebg p-6 rounded-full text-primary" data-id="element-107">
                  <Smartphone className="w-12 h-12" data-id="element-108" />
                </div>
                <span className="font-heading font-semibold text-title" data-id="element-109">
                  Celular
                </span>
              </div>
              <div className="flex flex-col items-center gap-4" data-id="element-110">
                <div className="bg-pagebg p-6 rounded-full text-primary" data-id="element-111">
                  <Monitor className="w-12 h-12" data-id="element-112" />
                </div>
                <span className="font-heading font-semibold text-title" data-id="element-113">
                  Computadora
                </span>
              </div>
              <div className="flex flex-col items-center gap-4" data-id="element-114">
                <div className="bg-pagebg p-6 rounded-full text-primary" data-id="element-115">
                  <Tablet className="w-12 h-12" data-id="element-116" />
                </div>
                <span className="font-heading font-semibold text-title" data-id="element-117">
                  Tablet
                </span>
              </div>
            </div>
            <p className="text-xl text-body font-medium" data-id="element-118">
              Accedé al contenido desde cualquier dispositivo y estudiá
              completamente a tu ritmo.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* SECTION 8: Resumen */}
      <section className="py-20 px-6" data-id="element-119">
        <div className="max-w-3xl mx-auto" data-id="element-120">
          <FadeIn data-id="element-121">
            <div className="bg-card rounded-[2rem] shadow-soft p-8 md:p-12 border border-gray-100" data-id="element-122">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-title text-center mb-8" data-id="element-123">
                Todo lo que recibís hoy
              </h2>
              <div className="space-y-4" data-id="element-124">
                {['6 Módulos completos', '6 Bonos exclusivos', 'Material descargable', 'Acceso inmediato', 'Compatible con todos los dispositivos', 'Recursos prácticos', 'Actualizaciones futuras (si aplica)'].map((item, i) => <div key={i} className="flex items-center gap-4 p-4 rounded-xl bg-pagebg/50" data-id="element-125">
                    <CheckCircle2 className="w-6 h-6 text-secondary flex-shrink-0" data-id="element-126" />
                    <span className="text-lg text-title font-medium" data-id="element-127">
                      {item}
                    </span>
                  </div>)}
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* SECTION 9: Oferta */}
      <section id="oferta" className="py-24 bg-white px-6" data-id="element-128">
        <div className="max-w-4xl mx-auto text-center" data-id="element-129">
          <FadeIn data-id="element-130">
            <div className="inline-block bg-red-100 text-red-600 font-bold px-6 py-2 rounded-full mb-8 animate-pulse" data-id="element-131">
              ⚠️ OFERTA ESPECIAL POR TIEMPO LIMITADO
            </div>

            <div className="mb-4" data-id="element-132">
              <span className="text-2xl text-gray-400 line-through font-heading font-semibold" data-id="element-133">
                Valor normal: $49.990
              </span>
            </div>

            <div className="mb-8" data-id="element-134">
              <span className="text-xl font-bold text-title block mb-2" data-id="element-135">
                SOLO HOY
              </span>
              <span className="text-6xl md:text-8xl font-heading font-extrabold text-primary tracking-tight" data-id="element-136">
                $14.990
              </span>
            </div>

            <div className="inline-flex items-center gap-2 bg-secondary/10 text-secondary font-bold text-lg px-6 py-3 rounded-2xl mb-12" data-id="element-137">
              🔥 Ahorrás $35.000
            </div>

            <button onClick={goToCheckout} className="w-full md:w-auto bg-accent hover:bg-accent-hover text-white font-heading font-bold text-xl md:text-2xl px-12 py-6 rounded-2xl shadow-[0_10px_40px_-10px_rgba(255,138,0,0.5)] hover:shadow-[0_10px_50px_-10px_rgba(255,138,0,0.7)] transition-all transform hover:-translate-y-2 flex items-center justify-center gap-3 mx-auto" data-id="element-138">
              👉 QUIERO ACCEDER AHORA
            </button>
          </FadeIn>
        </div>
      </section>

      {/* SECTION 10: Garantía / Seguridad */}
      <section className="py-16 bg-pagebg px-6 border-y border-gray-200" data-id="element-139">
        <div className="max-w-5xl mx-auto" data-id="element-140">
          <FadeIn data-id="element-141">
            <h3 className="text-center font-heading font-bold text-title text-2xl mb-10" data-id="element-142">
              Compra 100% Segura
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center" data-id="element-143">
              {[{
              icon: Clock,
              text: 'Acceso inmediato'
            }, {
              icon: Lock,
              text: 'Pago seguro'
            }, {
              icon: Download,
              text: 'Descarga digital'
            }, {
              icon: MonitorSmartphone,
              text: 'Disponible 24 hs'
            }].map((item, i) => <div key={i} className="flex flex-col items-center gap-3" data-id="element-144">
                  <div className="bg-white p-4 rounded-full shadow-sm text-primary" data-id="element-145">
                    <item.icon className="w-8 h-8" data-id="element-146" />
                  </div>
                  <span className="font-medium text-title" data-id="element-147">{item.text}</span>
                </div>)}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* SECTION 11: FAQ */}
      <section className="py-24 bg-white px-6" data-id="element-148">
        <div className="max-w-3xl mx-auto" data-id="element-149">
          <FadeIn data-id="element-150">
            <h2 className="text-3xl lg:text-4xl font-heading font-bold text-title text-center mb-12" data-id="element-151">
              Preguntas Frecuentes
            </h2>
            <div className="space-y-6" data-id="element-152">
              {[{
              q: '¿Necesito experiencia previa?',
              a: 'No. El contenido está pensado tanto para principiantes como para personas con experiencia.'
            }, {
              q: '¿Cómo recibiré el producto?',
              a: 'Una vez confirmado el pago recibirás acceso inmediato al contenido digital.'
            }, {
              q: '¿Puedo verlo desde el celular?',
              a: 'Sí. Es compatible con celular, tablet y computadora.'
            }, {
              q: '¿Tiene vencimiento?',
              a: 'Podrás consultarlo siempre que lo necesites.'
            }].map((faq, i) => <div key={i} className="bg-pagebg rounded-2xl p-6 border border-gray-100" data-id="element-153">
                  <h4 className="text-lg font-heading font-bold text-title mb-2 flex items-center gap-2" data-id="element-154">
                    <div className="w-2 h-2 rounded-full bg-primary" data-id="element-155"></div>
                    {faq.q}
                  </h4>
                  <p className="text-body pl-4" data-id="element-156">{faq.a}</p>
                </div>)}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* SECTION 12: Final CTA */}
      <section className="py-24 bg-primary px-6 text-center" data-id="element-157">
        <div className="max-w-4xl mx-auto" data-id="element-158">
          <FadeIn data-id="element-159">
            <h2 className="text-3xl md:text-5xl font-heading font-bold text-white mb-6 leading-tight" data-id="element-160">
              Empezá hoy a convertirte en un cuidador más seguro, preparado y
              profesional.
            </h2>
            <p className="text-xl text-white/90 mb-12 font-medium" data-id="element-161">
              Invertí en conocimientos que te acompañarán durante toda tu
              carrera como cuidador.
            </p>

            <div className="bg-white/10 p-8 rounded-3xl backdrop-blur-sm border border-white/20 inline-block mb-12" data-id="element-162">
              <div className="text-white/60 line-through text-xl mb-2 font-heading" data-id="element-163">
                $49.990
              </div>
              <div className="text-white text-5xl md:text-6xl font-heading font-extrabold" data-id="element-164">
                HOY $14.990
              </div>
            </div>

            <button onClick={goToCheckout} className="w-full md:w-auto bg-accent hover:bg-accent-hover text-white font-heading font-bold text-xl md:text-2xl px-12 py-6 rounded-2xl shadow-[0_10px_40px_-10px_rgba(255,138,0,0.5)] hover:shadow-[0_10px_50px_-10px_rgba(255,138,0,0.7)] transition-all transform hover:-translate-y-2 mx-auto block" data-id="element-165">
              QUIERO ACCEDER AL MEGA PACK
            </button>
          </FadeIn>
        </div>
      </section>

      {/* Social proof: verified purchase notifications */}
      <PurchaseNotification data-id="element-166" />
    </div>;
}